// src/components/Header.js
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import "./Header.css";

const PRIMARY_NAV_ITEMS = [
  { key: "dashboard", label: "Mission Control" },
  { key: "execution", label: "Mission Execution" },
  { key: "executive", label: "Executive" },
  { key: "posture", label: "Posture" },
  { key: "workflows", label: "Workflow" },
  { key: "validation", label: "Validation" },
  { key: "evidence", label: "Evidence" },
  { key: "sop", label: "SOP" },
  { key: "alerts", label: "Alerts" },
  { key: "drift", label: "Drift" },
  { key: "library", label: "Library" },
  { key: "administration", label: "Admin" },
  { key: "trust", label: "Trust" },
];

const APPEARANCE_OPTIONS = [
  { key: "dark", label: "Dark" },
  { key: "light", label: "Light" },
  { key: "system", label: "System" },
];

function scrollElementToTop(element) {
  if (!element) return;

  try {
    element.scrollTop = 0;
    element.scrollLeft = 0;
  } catch {
    // Ignore non-scrollable elements.
  }
}

function forceWindowTop() {
  if (typeof window === "undefined") return;

  const scrollNow = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }

    scrollElementToTop(document.scrollingElement);
    scrollElementToTop(document.documentElement);
    scrollElementToTop(document.body);
    scrollElementToTop(document.querySelector("#root"));
    scrollElementToTop(document.querySelector(".App"));
    scrollElementToTop(document.querySelector("main"));
  };

  const disableSmoothScroll = () => {
    try {
      const html = document.documentElement;
      const body = document.body;
      const previousHtmlBehavior = html.style.scrollBehavior;
      const previousBodyBehavior = body.style.scrollBehavior;

      html.style.scrollBehavior = "auto";
      body.style.scrollBehavior = "auto";

      return () => {
        html.style.scrollBehavior = previousHtmlBehavior;
        body.style.scrollBehavior = previousBodyBehavior;
      };
    } catch {
      return () => {};
    }
  };

  const restoreSmoothScroll = disableSmoothScroll();

  scrollNow();

  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => {
      scrollNow();
      window.requestAnimationFrame(scrollNow);
    });
  }

  [0, 25, 75, 150, 300, 600, 1000, 1500].forEach((delay) => {
    window.setTimeout(scrollNow, delay);
  });

  window.setTimeout(restoreSmoothScroll, 1600);
}

// Presentation only: prefer a person's Auth0 profile name over an account handle.
// No identity, authorization, or account state is modified here.
function getWelcomeFirstName(user) {
  const emailHandle = String(user?.email || "").split("@")[0].trim().toLowerCase();
  const readFirst = (value) => String(value || "").trim().split(/\s+/)[0];
  const givenName = readFirst(user?.given_name);
  if (givenName && !givenName.includes("@")) return givenName;

  for (const candidate of [user?.name, user?.nickname]) {
    const first = readFirst(candidate);
    if (first && !first.includes("@") && first.toLowerCase() !== emailHandle) {
      return first;
    }
  }
  // Keep existing demo-account behavior when Auth0 has no personal name.
  return emailHandle || "there";
}

export default function Header({
  onModeChange,
  currentMode,
  themePreference = "dark",
  onThemePreferenceChange,
  apiBaseUrl,
  tenantId,
  userId,
  getAccessToken,
}) {
  const { isAuthenticated, user, logout } = useAuth0();
  const [savedPersonalName, setSavedPersonalName] = useState("");
  const welcomeName = savedPersonalName || getWelcomeFirstName(user);
  const refreshPersonalName = useCallback(async () => {
    if (!isAuthenticated || !tenantId || !userId || !getAccessToken || !apiBaseUrl) {
      setSavedPersonalName("");
      return;
    }
    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBaseUrl}/account/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenantId,
          "X-User-Id": userId,
        },
      });
      if (!response.ok) throw new Error(`Profile read: ${response.status}`);
      const saved = await response.json();
      setSavedPersonalName(String(saved.preferred_name || saved.first_name || "").trim().split(/\s+/)[0] || "");
    } catch {
      // Graceful fallback to existing authenticated name, not stale saved identity.
      setSavedPersonalName("");
    }
  }, [isAuthenticated, tenantId, userId, getAccessToken, apiBaseUrl]);
  useEffect(() => {
    refreshPersonalName();
    window.addEventListener("oasys:personal-profile-updated", refreshPersonalName);
    return () => window.removeEventListener("oasys:personal-profile-updated", refreshPersonalName);
  }, [refreshPersonalName]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigate = useCallback(
    (mode) => {
      const shouldForceTop = mode === "dashboard" || mode === "execution";

      if (shouldForceTop) {
        const navNonce = String(Date.now());

        forceWindowTop();

        onModeChange(mode, {
          routeNavNonce: navNonce,
          scrollTop: "true",
        });

        forceWindowTop();

        window.setTimeout(() => {
          forceWindowTop();
        }, 50);

        window.setTimeout(() => {
          forceWindowTop();
        }, 250);

        window.setTimeout(() => {
          forceWindowTop();
        }, 750);

        return;
      }

      onModeChange(mode);
    },
    [onModeChange]
  );

  return (
    <>
    <aside className="oasys-side-navigation" aria-label="OASYS workspace navigation">
      <div
        className="branding"
        onClick={() => handleNavigate("dashboard")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleNavigate("dashboard");
        }}
      >
        <img src="/oasys-auth-logo-v2.png" alt="OASYS logo" className="logo" />
        <div className="brand-text">
          <h1 className="brand-title">OASYS</h1>
          <span className="brand-subtitle">Compliance Operations</span>
        </div>
      </div>

      <nav className="header-nav" aria-label="Primary">
        {PRIMARY_NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={currentMode === item.key ? "active" : ""}
            onClick={() => handleNavigate(item.key)}
            aria-current={currentMode === item.key ? "page" : undefined}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <button
        type="button"
        className="oasys-sidebar-ai-launcher"
        title="Toggle AI Assistant"
        aria-label="Toggle AI Assistant"
        aria-controls="advisor-dock"
        onClick={() => window.dispatchEvent(new Event("oasys:toggle-copilot"))}
      >
        <svg className="oasys-sidebar-ai-sparkles" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m12 2 1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2Z" />
          <path d="m19 16 .9 2.1L22 19l-2.1.9L19 22l-.9-2.1L16 19l2.1-.9L19 16Z" />
          <path d="m4 17 .6 1.4L6 19l-1.4.6L4 21l-.6-1.4L2 19l1.4-.6L4 17Z" />
        </svg>
        <span>AI Assistant</span>
      </button>
    </aside>

    <header className={`AppHeader oasys-shell-topbar${currentMode === "dashboard" ? " oasys-welcome-header" : " oasys-compact-account-header"}`} aria-label="Workspace header">
      {currentMode === "dashboard" ? (
        <div className="oasys-welcome-copy">
          <div className="oasys-welcome-eyebrow">Overview</div>
          <h2>Welcome back, {welcomeName}</h2>
          <p>Here&apos;s what&apos;s happening with your compliance program.</p>
        </div>
      ) : <div className="oasys-header-spacer" aria-hidden="true" />}

      <div className="user-menu" ref={menuRef}>
        {isAuthenticated && (
          <>
            <button
              className="user-button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Open user menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              {user.picture && !savedPersonalName ? (
                <img
                  src={user.picture}
                  alt="User avatar"
                  className="avatar-img"
                />
              ) : (
                <div className="avatar-fallback">
                  {String(welcomeName || "U").trim().charAt(0).toUpperCase() || "U"}
                </div>
              )}
            </button>

            {menuOpen && (
              <div className="dropdown" role="menu" aria-label="User menu">
                <div className="user-menu-identity">
                  <strong>{user.name || "OASYS User"}</strong>
                  {user.email ? <span>{user.email}</span> : null}
                </div>

                <button
                  type="button"
                  className="logout-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onModeChange("settings", { settingsSection: "profile" });
                  }}
                >
                  <span aria-hidden="true">◯</span>
                  <span>Edit Profile</span>
                </button>
                <div className="appearance-menu-section">
                  <div className="appearance-menu-heading">
                    <strong>Appearance</strong>
                  </div>

                  <div className="appearance-options" role="group" aria-label="Appearance">
                    {APPEARANCE_OPTIONS.map((option) => {
                      const selected = themePreference === option.key;

                      return (
                        <button
                          key={option.key}
                          type="button"
                          className={`appearance-option${selected ? " active" : ""}`}
                          onClick={() => onThemePreferenceChange?.(option.key)}
                          role="menuitemradio"
                          aria-checked={selected}
                        >
                          <span className="appearance-option-indicator" aria-hidden="true" />
                          <span className="appearance-option-copy">
                            <strong>{option.label}</strong>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="user-menu-divider" />

                <button
                  className="logout-menu-item"
                  onClick={() =>
                    logout({
                      logoutParams: {
                        returnTo: window.location.origin,
                      },
                    })
                  }
                  role="menuitem"
                >
                  <span aria-hidden="true">↪</span>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </header>
    </>
  );
}
